import EditIcon from "@mui/icons-material/Edit";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { type KeyboardEvent, type MouseEvent, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmailPreview from "@/components/campaignBuilder/design/EmailPreview";
import FullscreenPreviewOverlay from "@/components/campaignBuilder/design/FullscreenPreviewOverlay";
import LandingPreview from "@/components/campaignBuilder/design/landing/LandingPreview";
import { buildUbHeadline } from "@/components/campaignBuilder/design/unboxing/unboxing-helpers";
import GiftMessageModal from "@/components/giftExperience/GiftMessageModal";
import Unboxing from "@/components/giftExperience/Unboxing";
import { Confetti } from "@/components/redemption/experiential/Confetti";
import { primeConfettiAudio } from "@/components/redemption/experiential/confettiAudio";
import GiftVideoModal from "@/components/redemption/GiftVideoModal";
import { LANDING_CONFETTI_Z, UB_VIDEO_URL } from "@/constants/campaignBuilder";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useGiftPreviewFlow } from "@/hooks/useGiftPreviewFlow";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { getStepPath } from "@/lib/campaigns";
import { isSelfSendMode } from "@/utils/campaignMode-helper";
import { resolveAllTokens, resolveUnboxingHeadline } from "@/utils/emailPreview-helper";
import { resolveUnboxingLogoUrl } from "@/utils/unboxing-helper";
import ReviewCard from "../ReviewCard/ReviewCard";
import ReviewIconButton from "../ReviewIconButton/ReviewIconButton";

export default function RecipientExperienceCard() {
  const navigate = useNavigate();
  const { stage3Email, stage3Unboxing, stage3Landing, recipients, notificationMode, openSendTest } =
    useCampaignBuilder();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isMobile = useIsMobile();

  const unboxingLogoUrl = resolveUnboxingLogoUrl(stage3Email, stage3Landing, notificationMode);
  // Self-send (manual + universal): no GfG email, so the flow shows the link experience and the
  // "Send a test email" action is hidden.
  const isSelfSend = isSelfSendMode(notificationMode);
  const videoSrc = stage3Unboxing.customVideoUrl || UB_VIDEO_URL;

  // Sender tokens in the unboxing headline resolve against the first row that actually
  // filled each field, mirroring DesignStep's ubHeadline.
  const ubHeadline = resolveUnboxingHeadline(
    buildUbHeadline(stage3Unboxing),
    stage3Email.signatureHtml,
    recipients,
  );

  // Same for the gift message — the recipient reads it with its @tokens already resolved.
  const giftMessage = resolveAllTokens(
    stage3Unboxing.giftMessageText,
    stage3Email.signatureHtml,
    recipients,
  );

  // Local state, NOT the shared `designActiveTab` — advancing this preview must never
  // move Step 3's canvas tab.
  const flow = useGiftPreviewFlow({
    manual: isSelfSend,
    confettiEnabled: stage3Landing.confettiEnabled,
    confettiScheme: stage3Landing.confettiScheme,
  });

  // reset() runs in the same click handler as the open, so React batches both into one
  // commit — the overlay's first render is already clean (no stale modal/stage flash).
  const handlePreview = useCallback(() => {
    flow.reset();
    setIsPreviewOpen(true);
  }, [flow.reset]);
  const handleExitPreview = useCallback(() => setIsPreviewOpen(false), []);

  const handleShellKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handlePreview();
      }
    },
    [handlePreview],
  );

  const handleCtaClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      handlePreview();
    },
    [handlePreview],
  );

  const handleEdit = useCallback(() => {
    navigate(getStepPath(3));
  }, [navigate]);

  // One-shot rest-on-first-frame: the video has no `loop`, so on natural end
  // it stops on its last frame — this rewinds it back to frame 0 instead.
  const handleVideoEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  }, []);

  return (
    <ReviewCard compact>
      {/* biome-ignore lint/a11y/useSemanticElements: must wrap nested real <button>s (the actions and the CTA below) — a <button> can't contain a <button>, so this stays a div with role="button" (matches the prototype's .review-preview-shell). */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Preview the gift experience"
        onClick={handlePreview}
        onKeyDown={handleShellKeyDown}
        className="relative aspect-[5/2] cursor-pointer overflow-hidden rounded-[12px] border border-smoke"
      >
        {/* Icon actions over the top-right of the preview (`.review-preview-actions`).
            ReviewIconButton stops propagation so these don't also open the preview. */}
        <div className="absolute top-3 right-3 z-3 flex gap-2">
          {!isSelfSend && (
            <ReviewIconButton
              label="Send a test email"
              icon={<MailOutlineIcon sx={{ fontSize: 18 }} />}
              onClick={openSendTest}
            />
          )}
          <ReviewIconButton
            label="Edit the design"
            icon={<EditIcon sx={{ fontSize: 18 }} />}
            onClick={handleEdit}
          />
        </div>
        <video
          ref={videoRef}
          src={videoSrc}
          autoPlay
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
          aria-hidden="true"
          onEnded={handleVideoEnded}
          className="absolute inset-0 h-full w-full bg-white object-cover"
        />
        <div className="absolute inset-0 bg-[rgba(0,0,0,0.18)]" />
        <button
          type="button"
          onClick={handleCtaClick}
          className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 z-2 inline-flex cursor-pointer items-center gap-2.5 rounded-btn border border-smoke bg-white px-[22px] py-3 font-lato text-rodeo-black text-sm font-semibold shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-all duration-150 hover:-translate-y-[calc(50%+1px)] hover:bg-clear-dust hover:shadow-[0_10px_28px_rgba(0,0,0,0.14)]"
        >
          Preview Gift Experience
          <OpenInNewIcon sx={{ fontSize: 18, color: "var(--color-graphite)" }} />
        </button>
      </div>

      {isPreviewOpen && (
        <FullscreenPreviewOverlay
          onExit={handleExitPreview}
          fillContent={flow.stage === "unboxing"}
        >
          {flow.stage === "email" && (
            <EmailPreview
              state={stage3Email}
              isMobile={isMobile}
              recipients={recipients}
              onChooseGift={flow.advanceToUnboxing}
            />
          )}
          {flow.stage === "unboxing" && (
            <Unboxing
              isMobile={isMobile}
              partnerLogoUrl={unboxingLogoUrl}
              partnerLogoAlt="Partner"
              headline={ubHeadline}
              showGiftMessageButton={stage3Unboxing.showGiftMessage}
              skipUnboxing={stage3Unboxing.skipUnboxing}
              customVideoUrl={stage3Unboxing.customVideoUrl || undefined}
              onReadMessage={flow.openGiftMsg}
              playTrigger={flow.ubPlayTrigger}
              onVideoEnd={flow.handleUnboxingVideoEnd}
              isFullscreen
              withSound
              onUnwrap={primeConfettiAudio}
            />
          )}
          {flow.stage === "landing" && (
            <div className="w-full">
              <LandingPreview
                isMobile={isMobile}
                onReadMessage={flow.openGiftMsg}
                onPlayVideo={flow.openLpVideo}
                isFullscreen
              />
            </div>
          )}
        </FullscreenPreviewOverlay>
      )}

      {/* Owned at card level (mirrors DesignStep) so the backdrop covers the full screen. */}
      {isPreviewOpen && flow.giftMsgOpen && (
        <GiftMessageModal
          message={giftMessage || undefined}
          onClose={flow.closeGiftMsg}
          onUnwrap={flow.stage === "unboxing" ? flow.handleUnwrapMessage : undefined}
        />
      )}

      {/* Landing hero video modal */}
      {isPreviewOpen && (
        <GiftVideoModal
          open={flow.lpVideoOpen}
          videoUrl={stage3Landing.videoUrl || undefined}
          onClose={flow.closeLpVideo}
        />
      )}

      {/* Only mounted on the landing stage; z-index raised above the overlay in LANDING_CONFETTI_Z. */}
      {isPreviewOpen && flow.stage === "landing" && (
        <Confetti
          trigger={flow.confettiTrigger}
          shape={stage3Landing.confettiShape}
          colors={flow.confettiColors}
          zIndex={LANDING_CONFETTI_Z}
        />
      )}
    </ReviewCard>
  );
}
